import React, { FC } from "react";
import { styled } from "@mui/material";
import { ListPoint, Typography, ListItem, ListItemAvatar, Avatar, ListItemText } from "@/components";
import type { PointHistory } from "@/features/common/PointHistory/type";
import { COLORS } from "@/utils/colors";
import { getPointItemConfig } from "./getPointItemConfig";

export interface PointHistoryListProps {
  pointHistories: PointHistory[];
  isTop?: boolean;
}

export const PointHistoryList: FC<PointHistoryListProps> = ({ pointHistories, isTop }) => {
  return (
    <>
      {pointHistories.map((pointHistory: PointHistory, index: number) => {
        const currentMonth = new Date(pointHistory.updatedAt).getMonth();
        const previousMonth = index > 0 ? new Date(pointHistories[index - 1].updatedAt).getMonth() : -1;
        const isMinus = pointHistory.addPoint < 0;
        const config = getPointItemConfig(pointHistory.category, pointHistory.unspecifiedSubCategory, isMinus);

        return (
          <React.Fragment key={index}>
            {!isTop && (index === 0 || currentMonth !== previousMonth) && (
              <Typography variant="h2" textAlign="center" gutterBottom>
                {new Date(pointHistory.updatedAt).toLocaleString("ja-JP", {
                  year: "numeric",
                  month: "long",
                })}
              </Typography>
            )}
            <ListPoint variant={!isTop ? "card" : undefined}>
              <ListItem className="listPointItem">
                <ListItemAvatar>
                  <StyledAvatar color={config.iconColor}>{config.icon}</StyledAvatar>
                </ListItemAvatar>
                <StyledListItemText>
                  <Labels>
                    <span>{pointHistory.title}</span>
                    <Point minus={isMinus}>
                      {!isMinus && "+"}
                      {pointHistory.addPoint.toLocaleString()}
                      <span style={{ color: "initial" }}>P</span>
                    </Point>
                  </Labels>
                  {pointHistory.transferInfo && <Caption>{pointHistory.transferInfo}</Caption>}
                  <DateText color={config.logColor}>{pointHistory.logInfo}</DateText>
                </StyledListItemText>
              </ListItem>
            </ListPoint>
          </React.Fragment>
        );
      })}
    </>
  );
};

const StyledListItemText = styled(ListItemText)({
  margin: 0,
});

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (propName: PropertyKey) => propName !== "minus",
})<{ minus?: boolean }>(({ minus, color }) => ({
  width: "46px",
  height: "46px",
  backgroundColor: minus ? COLORS.NEGATIVE : color,
}));

const Labels = styled("span")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  fontWeight: 700,
  lineHeight: 1.4,
  marginBottom: theme.spacing(1),
}));

const Caption = styled("span")(({ theme }) => ({
  display: "block",
  fontSize: "0.875rem",
  lineHeight: 1.4,
  letterSpacing: "0.04em",
  color: COLORS.TEXT_WEAK,
  marginBottom: theme.spacing(1),
  wordBreak: "break-all",
  overflowWrap: "break-word",
}));

export const DateText = styled("span", {
  shouldForwardProp: (propName: PropertyKey) => propName !== "color",
})<{ color?: string }>(({ theme, color }) => ({
  display: "block",
  fontSize: "0.875rem",
  lineHeight: 1.4,
  letterSpacing: "0.04em",
  color: color || COLORS.TEXT_WEAK,

  "&:not(:last-of-type)": {
    marginBottom: theme.spacing(1),
  },
}));

const Point = styled("span", {
  shouldForwardProp: (propName: PropertyKey) => propName !== "minus",
})<{ minus?: boolean }>(({ minus }) => ({
  color: minus ? COLORS.DEFICIT : undefined,
  whiteSpace: "nowrap",
  marginLeft: "8px",
}));
